/**
 * SSO deep-link `state` (v1), per ADO 39669. AccessID passes base64url(JSON) through the
 * B2C sign-in; B2C hands it back in the redirect hash (`#state=...&id_token=...`). Only
 * validated ids are used to build the route — never a URL taken from `state`.
 */
import { notifyError } from './notify';

export interface DeepLink {
  projectId: string;
  collectionId?: string;
  resourceId?: string;
  query?: string;
}

export interface DeepLinkRoute {
  path: string;
  search: Record<string, string>;
}

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const MAX_QUERY = 500;

function decodeBase64Url(value: string): string {
  const b64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '='));
  return new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binary, (ch) => ch.charCodeAt(0)));
}

const isOptionalId = (v: unknown) => v === undefined || (typeof v === 'string' && OBJECT_ID.test(v));

/** Why `state` is invalid, or null when it's a valid v1 link. */
function validationError(data: any): string | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 'payload is not a JSON object';
  if (data.v !== 1) return `unsupported version v=${JSON.stringify(data.v)}`;
  const { p, c, r, q } = data;
  if (typeof p !== 'string' || !OBJECT_ID.test(p)) return 'p (project id) must be a 24-character hex id';
  if (!isOptionalId(c)) return 'c (collection id) must be a 24-character hex id';
  if (!isOptionalId(r)) return 'r (resource id) must be a 24-character hex id';
  if (r !== undefined && c === undefined) return 'r (resource id) requires c (collection id)';
  if (q !== undefined && (typeof q !== 'string' || q.length < 1 || q.length > MAX_QUERY)) {
    return `q (search) must be a string of 1-${MAX_QUERY} characters`;
  }
  return null;
}

/** Decodes and validates `state`, returning the link or the reason it was rejected. */
export function checkDeepLinkState(raw: string): { link: DeepLink } | { error: string } {
  let data: any;
  try {
    data = JSON.parse(decodeBase64Url(raw));
  } catch (e) {
    return { error: e instanceof SyntaxError ? 'payload is not valid JSON' : 'state is not valid base64url UTF-8' };
  }
  const error = validationError(data);
  if (error) return { error };
  return { link: { projectId: data.p, collectionId: data.c, resourceId: data.r, query: data.q } };
}

/** Decodes and validates `state`; anything invalid returns null (→ default landing). */
export function parseDeepLinkState(raw: string | null | undefined): DeepLink | null {
  if (!raw) return null;
  const result = checkDeepLinkState(raw);
  return 'link' in result ? result.link : null;
}

/** A resource link runs its own id search, so `q` only applies to project/collection links. */
export function deepLinkRoute(link: DeepLink): DeepLinkRoute {
  let path = `/project/${link.projectId}`;
  if (link.collectionId) path += `/collection/${link.collectionId}`;
  if (link.resourceId) return { path: `${path}/resource/${link.resourceId}`, search: {} };
  return { path, search: link.query ? { q: link.query } : {} };
}

let pending: DeepLinkRoute | null = null;
let invalid = false;

/** Called by main.js before Angular bootstraps and rewrites the hash. */
export function captureDeepLink(hash: string = window.location.hash): void {
  pending = null;
  invalid = false;
  const raw = new URLSearchParams(hash.replace(/^#/, '')).get('state');
  if (!raw) return;
  const result = checkDeepLinkState(raw);
  if ('link' in result) {
    pending = deepLinkRoute(result.link);
  } else {
    invalid = true;
    console.error(`[DeepLink] Ignoring invalid SSO deep-link state: ${result.error}`, raw);
  }
}

/**
 * Rewrites the raw B2C fragment into hashbang form in place (`#!/#id_token=...`). Otherwise
 * Angular pushes a new history entry for its own URL, leaving the id_token behind on Back.
 */
export function normalizeSsoHash(): void {
  const { hash } = window.location;
  if (hash.includes('id_token=') && !hash.startsWith('#!')) {
    window.history.replaceState(window.history.state, '', `#!/${hash}`);
  }
}

/**
 * Returns the captured route once; later calls get null. An invalid `state` gets an error
 * toast instead — deferred, since the run block calls this before the injector is set.
 */
export function takeDeepLinkRoute(): DeepLinkRoute | null {
  if (invalid) {
    invalid = false;
    setTimeout(() => notifyError({
      title: 'Invalid link',
      description: "The link you followed wasn't valid, so you've been taken to your projects.",
    }));
  }
  const route = pending;
  pending = null;
  return route;
}
